/**
 * Image Processor Utility
 * Handles mobile + web image responses
 * Prepares images safely for PDF generation
 */

/**
 * Normalize image type from URL or mime
 */
export const fitImageWithin = (width, height, maxDimension = 1280) => {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale)
  };
};

const detectImageType = (imageUrl = '', mimeType = '') => {
  if (mimeType) {
    if (mimeType.includes('png')) return 'PNG';
    if (mimeType.includes('heic')) return 'HEIC';
    return 'JPEG';
  }

  if (imageUrl?.endsWith('.png')) return 'PNG';
  if (imageUrl?.endsWith('.heic')) return 'HEIC';
  return 'JPEG';
};

/**
 * Processes raw image data from API response
 */
const processApiImageData = (apiResponse) => {
  if (!apiResponse || !Array.isArray(apiResponse.data)) {
    console.warn('❌ Invalid image API response:', apiResponse);
    return [];
  }

  return apiResponse.data.map((image) => {
    const imageUrl = image.imageUrl || image.url || image.fileUrl || null;

    return {
      id: image.id,
      photoType: image.photoType || image.photo_type || 'other',
      src: imageUrl,
      data: null, // will be filled if base64 is required
      type: detectImageType(imageUrl, image.mimeType),
      capturedAt: image.capturedAt || image.createdAt || null,
      remarks: image.remarks || '',
      location: {
        latitude:
          typeof image.latitude === 'number' ? image.latitude : null,
        longitude:
          typeof image.longitude === 'number' ? image.longitude : null
      }
    };
  });
};

/**
 * Group images by type
 */
const groupImagesByType = (images) => {
  if (!Array.isArray(images)) return {};

  return images.reduce((groups, image) => {
    const type = image.photoType || 'other';
    if (!groups[type]) groups[type] = [];
    groups[type].push(image);
    return groups;
  }, {});
};

/**
 * Human-readable labels
 */
const getImageTypeLabel = (type) => {
  const labels = {
    front_windscreen: 'Front Windscreen',
    front_left_corner: 'Front Left Corner',
    front_right_corner: 'Front Right Corner',
    rear_left_corner: 'Rear Left Corner',
    rear_right_corner: 'Rear Right Corner',
    odometer: 'Odometer',
    chassis_number: 'Chassis Number',
    engine_number: 'Engine Number',
    other: 'Other'
  };

  return (
    labels[type] ||
    type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

/**
 * Convert image URLs to Base64 (REQUIRED for many PDF libraries)
 */
const convertImagesToBase64 = async (images = []) => {
  for (const image of images) {
    if (!image?.src) continue;

    try {
      const response = await fetch(image.src);
      const buffer = await response.arrayBuffer();
      image.data = Buffer.from(buffer).toString('base64');
    } catch (error) {
      console.error('❌ Failed to fetch image:', image.src, error);
      image.data = null;
    }
  }

  return images;
};

/**
 * Prepare images for PDF
 */
const prepareImagesForPdf = async (apiResponse, useBase64 = true) => {
  console.log('📸 RAW IMAGE API RESPONSE:', apiResponse);

  let images = processApiImageData(apiResponse);

  if (useBase64) {
    images = await convertImagesToBase64(images);
  }

  const groupedImages = groupImagesByType(images);

  return {
    images,
    groupedImages,
    count: images.length,
    hasImages: images.length > 0
  };
};

/**
 * Export
 */
const imageProcessor = {
  processApiImageData,
  groupImagesByType,
  getImageTypeLabel,
  convertImagesToBase64,
  prepareImagesForPdf
};

export default imageProcessor;
